import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';

  const navLinks = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.categories'), href: '/category/football' },
    { label: t('nav.articles'), href: '/articles' },
    { label: t('nav.about'), href: '/about' },
  ];

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Spacer for fixed nav */}
      <div className="h-16"></div>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center">
                <i className="ri-vip-crown-line text-background-50 text-lg"></i>
              </div>
              <span className={`font-heading font-bold text-xl tracking-tight ${scrolled || !isHome ? 'text-foreground-900' : 'text-background-50'}`}>
                皇冠体育
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    isActive(link.href)
                      ? 'bg-primary-500 text-background-50'
                      : scrolled || !isHome
                        ? 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                        : 'text-background-50/90 hover:text-background-50 hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <a
                href="#"
                className={`hidden md:flex items-center gap-2 px-5 py-2 rounded-full font-medium text-sm transition-all whitespace-nowrap ${
                  scrolled || !isHome
                    ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                    : 'bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 hover:bg-background-50/30'
                }`}
              >
                <i className="ri-download-2-line"></i>
                {t('nav.download')}
              </a>

              {/* Mobile menu button */}
              <button
                className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
                  scrolled || !isHome ? 'text-foreground-700' : 'text-background-50'
                }`}
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="菜单"
              >
                <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200">
            <div className="px-4 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive(link.href)
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="#"
                className="mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent-500 text-background-50 font-medium text-sm"
              >
                <i className="ri-download-2-line"></i>
                {t('nav.download')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}